<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-content">
      <div class="modal-header">
        <h3 v-if="!pendingRoom || !requiresTwoFactor">Select Room</h3>
        <h3 v-else>Secure Verification Required</h3>
        <button @click="close" class="close-btn">&times;</button>
      </div>

      <div class="modal-body">
        <!-- 2FA Verification Step -->
        <div v-if="pendingRoom && requiresTwoFactor && !twoFactorVerified" class="twofactor-section">
          <div class="twofactor-card">
            <div class="twofactor-header">
              <font-awesome-icon icon="fa-solid fa-shield-halved" class="shield-icon" />
              <h4>Two-Factor Authentication Required</h4>
            </div>
            <p class="twofactor-message">
              Room {{ pendingRoom.roomNumber }} requires guest verification before proceeding with the sale.
            </p>

            <div class="verification-step">
              <div v-if="!otpSent" class="otp-initiation">
                <div class="charge-summary">
                  <h5>Sale Information</h5>
                  <div class="guest-display">
                    <div class="guest-name">{{ pendingRoom.guestName }}</div>
                    <div class="room-number-display">Room {{ pendingRoom.roomNumber }}</div>
                    <div class="sale-amount">
                      <font-awesome-icon icon="fa-solid fa-money-bill-wave" />
                      Amount: {{ $utils.formatMoney($store.getters.getSaleTotal || 0) }}
                    </div>
                  </div>
                </div>
                <button
                  @click="initiateTwoFactor"
                  class="btn btn-primary btn-lg"
                  :disabled="loading"
                >
                  <font-awesome-icon icon="fa-solid fa-paper-plane" />
                  <span v-if="loading">Sending...</span>
                  <span v-else>Send Verification Code</span>
                </button>
              </div>

              <div v-else class="otp-verification">
                <div class="otp-info">
                  <font-awesome-icon icon="fa-solid fa-circle-info" />
                  <span>{{ otpMessage || 'Verification code sent to guest' }}</span>
                </div>

                <div class="otp-input-group">
                  <label>
                    <font-awesome-icon icon="fa-solid fa-key" />
                    Enter Verification Code
                  </label>
                  <input
                    type="text"
                    v-model="otpCode"
                    class="form-input otp-input"
                    placeholder="Enter 6-digit code"
                    maxlength="6"
                    @input="validateOtpInput"
                  />
                  <small class="input-help">
                    <font-awesome-icon icon="fa-solid fa-clock" />
                    Code expires in {{ otpCountdown }} seconds
                  </small>
                </div>

                <div class="otp-actions">
                  <button
                    @click="verifyOtpCode"
                    class="btn btn-success"
                    :disabled="!otpCode || otpCode.length !== 6 || loading"
                  >
                    <font-awesome-icon icon="fa-solid fa-check-circle" />
                    <span v-if="loading">Verifying...</span>
                    <span v-else>Verify & Continue</span>
                  </button>
                  <button
                    @click="resendOtp"
                    class="btn btn-outline-secondary"
                    :disabled="loading || otpCountdown > 0"
                  >
                    <font-awesome-icon icon="fa-solid fa-rotate" />
                    Resend Code
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="loading" class="loading">
          <div class="spinner"></div>
          <p>Loading occupied rooms...</p>
        </div>

        <div v-else-if="error" class="error-message">
          <p>{{ error }}</p>
          <button @click="loadOccupiedRooms" class="retry-btn">Retry</button>
        </div>

        <div v-else-if="rooms.length === 0" class="empty-state">
          <p>No occupied rooms found</p>
        </div>

        <div v-else class="rooms-list">
          <div
            v-for="room in rooms"
            :key="room.roomId"
            @click="selectRoom(room)"
            class="room-card"
          >
            <div class="room-info">
              <div class="room-number">
                {{ room.roomNumber }}
                <span v-if="room.serviceTwoFactorEnabled" class="twofa-badge">
                  <font-awesome-icon icon="fa-solid fa-shield-halved" />
                  2FA
                </span>
              </div>
              <div class="room-guest">{{ room.guestName }}</div>
              <div class="room-dates">
                In: {{ formatDate(room.checkInDate) }} | Out: {{ formatDate(room.checkOutDate) }}
              </div>
            </div>
            <div class="room-arrow">
              <font-awesome-icon icon="fa-solid fa-chevron-right" />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="close" class="cancel-btn">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import LodgingIntegration from '@/services/lodgingIntegration';

export default {
  name: 'RoomSelectionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rooms: [],
      loading: false,
      error: null,
      pendingRoom: null,
      requiresTwoFactor: false,
      twoFactorVerified: false,
      otpSent: false,
      otpCode: '',
      otpCountdown: 0,
      otpTimer: null,
      transactionId: null,
      otpMessage: ''
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.resetState();
          this.loadOccupiedRooms();
          this.loadSaleLinesFromStorage();
        }
      }
    }
  },

  mounted() {
    // Ensure sale lines are loaded from storage when component mounts
    this.loadSaleLinesFromStorage();
  },

  created() {
    // Also load on creation as backup
    this.loadSaleLinesFromStorage();
  },
  methods: {
    async loadOccupiedRooms() {
      this.loading = true;
      this.error = null;

      try {
        this.rooms = await LodgingIntegration.getOccupiedRooms();
      } catch (error) {
        console.error('Failed to load occupied rooms:', error);
        this.error = error.message || 'Failed to load occupied rooms';
      } finally {
        this.loading = false;
      }
    },

    selectRoom(room) {
      // Check if 2FA is required
      if (room.serviceTwoFactorEnabled) {
        this.pendingRoom = room;
        this.requiresTwoFactor = true;
        this.twoFactorVerified = false;
      } else {
        // No 2FA required, emit immediately
        this.$emit('room-selected', room);
      }
    },

    async initiateTwoFactor() {
      this.loading = true;
      try {
        const bookingId = this.pendingRoom.bookingId || this.pendingRoom.id;

        if (!bookingId) {
          throw new Error('Booking ID is required');
        }

        const saleLines = this.$store.getters['sales/getSaleLines'] || [];
        let saleTotal = 0;
        const getterTotal = this.$store.getters['sales/getSaleTotal'] || 0;

        // Method 2: Calculate manually from sale lines
        if (saleLines.length > 0) {
          saleTotal = saleLines.reduce((sum, line) => {
            let lineTotal = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0)) - Number(line.Discount || 0);

            // Add instruction costs if any
            if (line.instructions && line.instructions.length > 0) {
              const instructionTotal = line.instructions.reduce((acc, instr) => acc + (Number(instr.Price) * Number(instr.count || 1)), 0);
              lineTotal += instructionTotal;
            }

            return sum + lineTotal;
          }, 0);
        }

        // Use the calculated total, fallback to getter if calculated is 0
        const finalTotal = saleTotal > 0 ? saleTotal : getterTotal;

        console.log('=== 2FA Amount Debug ===');
        console.log('Sale Lines Count:', saleLines.length);
        console.log('Sale Lines:', saleLines);
        console.log('Getter Total:', getterTotal);
        console.log('Calculated Total:', saleTotal);
        console.log('Final Total:', finalTotal);
        console.log('========================');

        // Collect departments from sale lines (unique values)
        const departments = [...new Set(saleLines.map(line => line.Department || 'RESTAURANT'))];

        // Initiate 2FA with charge details
        const chargeData = {
          description: 'Restaurant Order',
          amount: Number(finalTotal),
          billTotal: Number(finalTotal),
          tipAmount: 0,
          itemType: 'RESTAURANT',
          department: departments.length > 0 ? departments : ['RESTAURANT'],
          invoiceRef: null
        };

        console.log('Initiating 2FA for room selection');
        console.log('- Charge Data:', chargeData);
        console.log('- Room Details:', this.pendingRoom);

        // Validate amount is greater than 0
        if (Number(finalTotal) <= 0) {
          console.warn('⚠️ WARNING: Sale total is 0 or negative!');
          console.warn('Sale may not have been added to cart yet.');
          // Continue anyway - the API will handle it, but log it
        }

        const response = await LodgingIntegration.initiateSecureCharge(bookingId, chargeData);

        this.transactionId = response.transactionId;
        this.otpSent = true;
        this.otpMessage = response.message || 'Verification code sent to guest via Email and WhatsApp';

        // Start countdown
        this.startOtpCountdown();

        console.log('2FA initiated for room selection, transaction ID:', this.transactionId);

      } catch (error) {
        console.error('Failed to initiate 2FA:', error);
        Swal.fire({
          title: 'Verification Failed',
          text: error.message || 'Failed to send verification code',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.loading = false;
      }
    },

    async verifyOtpCode() {
      if (!this.otpCode || this.otpCode.length !== 6) {
        return;
      }

      this.loading = true;
      try {
        const response = await LodgingIntegration.verifySecureCharge(this.transactionId, this.otpCode);

        if (response.status === 'verified') {
          this.twoFactorVerified = true;
          this.otpSent = false;

          if (this.otpTimer) {
            clearInterval(this.otpTimer);
            this.otpTimer = null;
          }

          Swal.fire({
            title: 'Verification Successful',
            text: 'Code verified. You can now proceed with the sale.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });

          // Emit the room-selected event with transaction ID for invoice
          const roomWithTransactionId = {
            ...this.pendingRoom,
            bookingOtpTransactionId: this.transactionId
          };
          this.$emit('room-selected', roomWithTransactionId);
          this.close();
        }

      } catch (error) {
        console.error('OTP verification failed:', error);

        let errorMessage = 'Invalid verification code';
        if (error.response?.status === 410) {
          errorMessage = 'Verification code has expired';
        } else if (error.response?.status === 400) {
          errorMessage = 'Invalid transaction or verification attempt';
        }

        Swal.fire({
          title: 'Verification Failed',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });

        // Clear the code so user can try again
        this.otpCode = '';
      } finally {
        this.loading = false;
      }
    },

    async resendOtp() {
      await this.initiateTwoFactor();
      this.otpCode = '';
    },

    validateOtpInput() {
      // Only allow digits
      this.otpCode = this.otpCode.replace(/\D/g, '');
    },

    startOtpCountdown() {
      this.otpCountdown = 120; // 2 minutes

      if (this.otpTimer) {
        clearInterval(this.otpTimer);
      }

      this.otpTimer = setInterval(() => {
        this.otpCountdown--;

        if (this.otpCountdown <= 0) {
          clearInterval(this.otpTimer);
          this.otpTimer = null;
        }
      }, 1000);
    },

    resetState() {
      this.pendingRoom = null;
      this.requiresTwoFactor = false;
      this.twoFactorVerified = false;
      this.otpSent = false;
      this.otpCode = '';
      this.otpCountdown = 0;
      this.transactionId = null;
      this.otpMessage = '';
      if (this.otpTimer) {
        clearInterval(this.otpTimer);
        this.otpTimer = null;
      }
    },

    loadSaleLinesFromStorage() {
      // Explicitly load sale lines from localStorage as backup
      const savedSaleLines = localStorage.getItem('currentSaleLines');
      if (savedSaleLines) {
        try {
          const parsedLines = JSON.parse(savedSaleLines);
          console.log('📦 Loaded sale lines from localStorage:', parsedLines);
          // If Vuex store is empty but localStorage has data, log the discrepancy
          const storeLines = this.$store.getters['sales/getSaleLines'] || [];
          if (storeLines.length === 0 && parsedLines.length > 0) {
            console.warn('⚠️ WARNING: Vuex store is empty but localStorage has sale lines!');
            console.warn('Store lines:', storeLines);
            console.warn('localStorage lines:', parsedLines);
          }
        } catch (error) {
          console.error('Error parsing sale lines from localStorage:', error);
        }
      } else {
        console.log('ℹ️ No sale lines found in localStorage');
      }
    },

    close() {
      this.resetState();
      this.$emit('close');
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 16px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: var(--card-shadow-hover);
  border: var(--card-border);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: var(--transition-speed);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.loading {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 4px solid var(--card-border);
  border-top: 4px solid var(--accent-electric-blue);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  padding: 40px;
  color: #dc3545;
}

.retry-btn {
  margin-top: 15px;
  padding: 10px 20px;
  background: var(--accent-electric-blue);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: var(--transition-speed);
}

.retry-btn:hover {
  background: var(--accent-electric-blue-dark);
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--text-secondary);
}

.rooms-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.room-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--bg-primary);
  border: 2px solid var(--card-border);
  border-radius: 12px;
  cursor: pointer;
  transition: var(--transition-speed);
}

.room-card:hover {
  border-color: var(--accent-electric-blue);
  transform: translateY(-2px);
  box-shadow: var(--card-shadow-hover);
}

.room-info {
  flex: 1;
}

.room-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--accent-electric-blue);
  margin-bottom: 4px;
}

.room-guest {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.room-dates {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.room-arrow {
  color: var(--text-secondary);
  font-size: 1.2rem;
  margin-left: 16px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid var(--card-border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn {
  padding: 12px 24px;
  background: var(--secondary-button-bg);
  color: var(--secondary-button-text);
  border: var(--card-border);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: var(--transition-speed);
}

.cancel-btn:hover {
  background: var(--secondary-button-bg-dark);
}

/* 2FA Badge for Rooms */
.twofa-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  margin-left: 8px;
  background: rgba(var(--accent-electric-blue-rgb), 0.15);
  border: 1px solid var(--accent-electric-blue);
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--accent-electric-blue);
}

.twofa-badge svg {
  font-size: 0.75rem;
}

/* Two-Factor Authentication Styles */
.twofactor-section {
  animation: fadeInUp 0.4s ease-out;
}

.twofactor-card {
  background: linear-gradient(135deg, rgba(var(--accent-electric-blue-rgb), 0.08), rgba(var(--accent-neon-purple-rgb), 0.08));
  border: 2px solid var(--accent-electric-blue);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
}

.twofactor-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.twofactor-header h4 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 700;
}

.shield-icon {
  font-size: 3rem;
  color: var(--accent-electric-blue);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.twofactor-message {
  color: var(--text-secondary);
  font-size: 1rem;
  margin-bottom: 24px;
  line-height: 1.6;
}

.verification-step {
  margin-top: 24px;
}

.otp-initiation {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.charge-summary {
  background: var(--card-bg);
  border: 2px solid var(--accent-electric-blue);
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 350px;
}

.charge-summary h5 {
  margin: 0 0 12px 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
}

.guest-display {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.guest-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
}

.room-number-display {
  font-size: 1.2rem;
  color: var(--accent-electric-blue);
  font-weight: 600;
}

.sale-amount {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--success);
}

.sale-amount svg {
  font-size: 1.1rem;
}

.btn-lg {
  padding: 18px 36px;
  font-size: 1.1rem;
  width: 100%;
  max-width: 350px;
}

.btn {
  padding: 14px 28px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb), 0.2);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--accent-electric-blue-rgb), 0.35);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.otp-verification {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.otp-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: rgba(var(--accent-electric-blue-rgb), 0.1);
  border: 1px solid var(--accent-electric-blue);
  border-radius: 10px;
  color: var(--accent-electric-blue);
  font-weight: 600;
}

.otp-info svg {
  font-size: 1.2rem;
}

.otp-input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
}

.otp-input-group label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
}

.otp-input {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 8px;
  font-weight: 700;
  padding: 16px;
  background: var(--card-bg);
  border: 2px solid var(--accent-electric-blue);
  border-radius: 12px;
  color: var(--text-primary);
}

.otp-input:focus {
  border-color: var(--accent-electric-blue);
  box-shadow: 0 0 0 4px rgba(var(--accent-electric-blue-rgb), 0.15);
}

.input-help {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-top: 4px;
}

.otp-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.otp-actions .btn {
  min-width: 180px;
}

.btn-success {
  background: linear-gradient(135deg, var(--success), #20c997);
  color: white;
  box-shadow: 0 4px 12px rgba(var(--success-rgb), 0.2);
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--success-rgb), 0.35);
}

.btn-success:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-outline-secondary {
  background: transparent;
  border: 2px solid var(--text-secondary);
  color: var(--text-secondary);
}

.btn-outline-secondary:hover:not(:disabled) {
  background: var(--text-secondary);
  color: var(--card-bg);
  transform: translateY(-2px);
}

.btn-outline-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
